/**
 * Script Behavior
 *
 * TraceBehavior subclass that wraps a user-authored TypeScript script.
 * Executable instances own a compiled closure and a ScriptContext.
 *
 * This class has no static `behaviorType` — it handles ANY @type that maps
 * to a script in the ScriptRegistry. It is NOT registered in BehaviorRegistry.
 */

import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Observer } from "@babylonjs/core/Misc/observable";
import { Scene } from "@babylonjs/core/scene";
import { TraceBehavior } from "../behaviors/tracebehavior";
import { BehaviorData } from "../scenedata/behaviordata";
import { Logger } from "../utils/Logger";
import type { CompiledScript, ScriptFactory } from "./ScriptCompiler";
import { createScriptContext } from "./ScriptContext";

export class ScriptBehavior extends TraceBehavior<BehaviorData> {
    private _scriptInstance: CompiledScript | null = null;
    private _renderObserver: Observer<Scene> | null = null;
    private _startTime: number = 0;
    private _started: boolean = false;

    constructor(
        sceneObject: import("../core/SceneObject").SceneObject,
        data: BehaviorData,
        private _factory?: ScriptFactory,
    ) {
        super(sceneObject, data);
        if (sceneObject.traceScene.scriptRegistry?.executionEnabled === false) this._factory = undefined;
    }

    /**
     * Create the script instance from the factory.
     * Each ScriptBehavior gets its own closure (fresh module-level state).
     */
    load(): void {
        if (!this._factory) return;
        try {
            this._scriptInstance = this._factory();
        } catch (err) {
            Logger.error(`[ScriptBehavior:${this._resolved['@type']}] Factory error:`, err);
        }
    }

    protected onAttach(_target: TransformNode): void {
        this._startTime = performance.now() / 1000;

        // If play mode is already active, fire onStart immediately
        if (this.sceneObject.traceScene.isPlayMode()) {
            this._fireStart();
        }

        // Register per-frame update if the script has onUpdate
        if (this._scriptInstance?.onUpdate && this.scene) {
            this._registerUpdateObserver();
        }
    }

    protected onDetach(): void {
        this._teardown();
    }

    // ── Public hook entry points (called by TraceScene event routing) ──

    /** Called when play mode starts (or immediately if already playing) */
    fireStart(): void {
        this._fireStart();
    }

    /** Called when play mode stops — fires onDestroy, removes update observer, resets state */
    fireStop(): void {
        this._teardown();
    }

    /** Called by TraceScene.handlePointerEvent for select events */
    async fireSelect(isEnter: boolean): Promise<void> {
        const hook = isEnter ? this._scriptInstance?.onSelect : this._scriptInstance?.onSelectExit;
        if (!hook || !this._started) return;
        const elapsed = performance.now() / 1000 - this._startTime;
        const ctx = this._buildContext(0, elapsed);
        try {
            await hook(ctx);
        } catch (err) {
            Logger.error(`[ScriptBehavior:${this._resolved['@type']}] onSelect error:`, err);
        }
    }

    /** Called by TraceScene.handlePointerEvent for hover events */
    async fireHover(isEnter: boolean): Promise<void> {
        const hook = isEnter ? this._scriptInstance?.onHover : this._scriptInstance?.onHoverExit;
        if (!hook || !this._started) return;
        const elapsed = performance.now() / 1000 - this._startTime;
        const ctx = this._buildContext(0, elapsed);
        try {
            await hook(ctx);
        } catch (err) {
            Logger.error(`[ScriptBehavior:${this._resolved['@type']}] onHover error:`, err);
        }
    }

    /** Called when another script or Interactions dispatches an event */
    async fireEvent(eventName: string): Promise<void> {
        if (!this._scriptInstance?.onEvent || !this._started) return;
        const elapsed = performance.now() / 1000 - this._startTime;
        const ctx = this._buildContext(0, elapsed);
        try {
            await this._scriptInstance.onEvent(ctx, eventName);
        } catch (err) {
            Logger.error(`[ScriptBehavior:${this._resolved['@type']}] onEvent error:`, err);
        }
    }

    /**
     * Hot-reload the script with a new factory.
     * Detaches the current script instance (fires onDestroy) and creates
     * a fresh one from the updated factory (fires onStart if playing).
     */
    reloadFactory(factory: ScriptFactory): void {
        // A passive behavior cannot be promoted into an executable one.
        if (!this._factory) return;
        this._teardown();

        // Create new instance
        this._factory = factory;
        try {
            this._scriptInstance = this._factory();
        } catch (err) {
            Logger.error(`[ScriptBehavior:${this._resolved['@type']}] Factory error during reload:`, err);
            this._scriptInstance = null;
            return;
        }

        // Re-register update observer if needed
        if (this._scriptInstance?.onUpdate && this.scene) {
            this._registerUpdateObserver();
        }

        // If play mode is active, fire onStart
        if (this.sceneObject.traceScene.isPlayMode()) {
            this._fireStart();
        }
    }

    // ── Internals ────────────────────────────────────────

    /** Fire onDestroy, remove render observer, reset started state. */
    private _teardown(): void {
        if (this._started && this._scriptInstance?.onDestroy) {
            const elapsed = performance.now() / 1000 - this._startTime;
            const ctx = this._buildContext(0, elapsed);
            try {
                this._scriptInstance.onDestroy(ctx);
            } catch (err) {
                Logger.error(`[ScriptBehavior:${this._resolved['@type']}] onDestroy error:`, err);
            }
        }

        if (this._renderObserver && this.scene) {
            this.scene.onBeforeRenderObservable.remove(this._renderObserver);
            this._renderObserver = null;
        }
        this._started = false;
    }

    private _fireStart(): void {
        if (this._started || !this._scriptInstance) return;
        this._started = true;
        this._startTime = performance.now() / 1000;

        // Re-register update observer if it was removed (e.g., by fireStop)
        if (this._scriptInstance.onUpdate && this.scene && !this._renderObserver) {
            this._registerUpdateObserver();
        }

        if (this._scriptInstance.onStart) {
            const ctx = this._buildContext(0, 0);
            try {
                const result = this._scriptInstance.onStart(ctx);
                if (result instanceof Promise) {
                    result.catch(err =>
                        Logger.error(`[ScriptBehavior:${this._resolved['@type']}] onStart error:`, err)
                    );
                }
            } catch (err) {
                Logger.error(`[ScriptBehavior:${this._resolved['@type']}] onStart error:`, err);
            }
        }
    }

    private _registerUpdateObserver(): void {
        if (!this.scene || !this._scriptInstance?.onUpdate) return;

        // Guard against duplicate observers
        if (this._renderObserver) {
            this.scene.onBeforeRenderObservable.remove(this._renderObserver);
        }

        this._renderObserver = this.scene.onBeforeRenderObservable.add(() => {
            if (!this.scene || !this._started || !this._scriptInstance?.onUpdate) return;
            const dt = this.scene.getEngine().getDeltaTime() / 1000;
            const elapsed = performance.now() / 1000 - this._startTime;
            const ctx = this._buildContext(dt, elapsed);
            try {
                this._scriptInstance.onUpdate(ctx);
            } catch (err) {
                Logger.error(`[ScriptBehavior:${this._resolved['@type']}] onUpdate error:`, err);
                // Remove observer on repeated errors to avoid log spam
                // The script is broken — stop calling it
                if (this._renderObserver && this.scene) {
                    this.scene.onBeforeRenderObservable.remove(this._renderObserver);
                    this._renderObserver = null;
                }
            }
        });
    }

    private _buildContext(deltaTime: number, elapsed: number) {
        return createScriptContext(
            this.sceneObject,
            this.sceneObject.traceScene,
            this._resolved,
            { deltaTime, elapsed },
            this._resolved['@type'],
        );
    }
}
